import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { useToast } from '../ui/ToastProvider'

function formatDt(iso) {
  return formatApiDateTimeDisplay(undefined, iso)
}

export default function WithdrawalApprovalRequestsPage() {
  const { show } = useToast()
  const [items, setItems] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/users/withdrawal-requests', { params: { page: 0, size: 100 } })
      const payload = res?.data?.data ?? res?.data
      const content = payload?.content ?? []
      setItems(Array.isArray(content) ? content : [])
      if (typeof payload?.totalElements === 'number') setTotalElements(payload.totalElements)
    } catch (e) {
      setError('탈퇴 승인 대기 목록을 불러오지 못했습니다.')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const approveWithdrawal = async (userId) => {
    const ok = window.confirm('해당 사용자 계정을 탈퇴(비식별화) 처리합니다. 계속할까요?')
    if (!ok) return
    setBusyId(userId)
    try {
      await api.post(`/users/${userId}/withdrawal`, {})
      show('탈퇴 처리되었습니다.', 'success')
      await load()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '처리에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">탈퇴 승인</h1>
            <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
          </div>
        </div>
        <button type="button" onClick={load} className="rounded border px-3 py-2 text-sm" disabled={loading}>
          새로고침
        </button>
      </div>

      {typeof totalElements === 'number' && totalElements > 0 && (
        <div className="text-sm text-slate-600">대기 <span className="font-semibold text-slate-900">{totalElements}</span>건</div>
      )}

      {loading && items.length === 0 ? (
        <Loading />
      ) : error ? (
        <InlineError message={error} />
      ) : items.length === 0 ? (
        <div className="rounded border bg-slate-50 px-4 py-8 text-center text-slate-600 text-sm">
          탈퇴 승인 대기 건이 없습니다.
        </div>
      ) : (
        <div className="rounded border overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-slate-700">접수일시</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">사용자</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">이메일</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">문의</th>
                <th className="text-right px-3 py-2 font-medium text-slate-700">처리</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((row) => (
                <tr key={row.inquiryId} className="hover:bg-slate-50/80">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatDt(row.createdAt)}</td>
                  <td className="px-3 py-2 text-slate-900">{row.userName || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.userEmail || '-'}</td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/support/inquiries/${row.inquiryId}`}
                      className="text-brand-sky hover:underline"
                    >
                      내용 보기
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="rounded bg-red-600 text-white px-3 py-1.5 text-xs disabled:opacity-50"
                      disabled={!row.userId || busyId === row.userId}
                      onClick={() => approveWithdrawal(row.userId)}
                    >
                      {busyId === row.userId ? '처리 중…' : '탈퇴 승인'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
